import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, LogOut, Sun, Moon, Monitor, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { NavHeader } from "@/components/nav-header";
import { useAuth } from "@/lib/auth-provider";
import { deleteAccount, displayNameOf, signOut } from "@/lib/api";
import { demoOwnerFromUser } from "@/lib/demo/demo-trip";
import { startTour } from "@/lib/onboarding/tour-state";
import { useTheme } from "@/lib/theme-provider";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export default function ProfileRoute() {
  const { user } = useAuth();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const displayName = displayNameOf(user, "There");

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out. See you soon.");
    navigate("/auth", { replace: true });
  };

  // Errors are toasted and the dialog stays open, so the user can retry.
  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
    } catch {
      toast.error("Couldn't delete your account. Try again.");
      return;
    }
    setDeleteOpen(false);
    toast.success("Your account has been deleted.");
    navigate("/auth", { replace: true });
  };

  return (
    <main className="min-h-full bg-bg pb-[calc(48px+var(--bottom-tabs-h,0px))]">
      <NavHeader title="Profile" showBack maxWidthClassName="max-w-md" />

      <div className="mx-auto max-w-md px-5 pt-6 flex flex-col gap-6">
        {/* Header */}
        <section className="flex flex-col items-center gap-3 text-center">
          <Avatar name={displayName} size="xl" />
          <div>
            <h2
              className="font-display text-2xl leading-tight tracking-tight text-ink m-0"
              style={{ fontVariationSettings: `"opsz" 48, "wght" 500` }}
            >
              {displayName}
            </h2>
            <p className="mt-1 text-[13px] text-ink-muted">{user?.email}</p>
          </div>
        </section>

        {/* Appearance */}
        <section>
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
            Appearance
          </p>
          <div className="grid grid-cols-3 gap-1 rounded-full bg-bg-sunken p-1">
            {(
              [
                { key: "light", label: "Light", Icon: Sun },
                { key: "system", label: "System", Icon: Monitor },
                { key: "dark", label: "Dark", Icon: Moon },
              ] as const
            ).map(({ key, label, Icon }) => {
              const on = mode === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMode(key)}
                  className={cn(
                    "inline-flex items-center justify-center gap-1.5 rounded-full py-2 text-[13px] font-semibold transition-colors",
                    on ? "bg-bg-elevated text-ink shadow-warm-1" : "text-ink-subtle hover:text-ink",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Help */}
        <section>
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
            Help
          </p>
          <div className="overflow-hidden rounded-xl border border-line bg-bg-elevated">
            <Button
              variant="ghost"
              block
              onClick={() => user && startTour(demoOwnerFromUser(user), "replay")}
              className="h-14 justify-between rounded-none px-4 text-ink hover:bg-bg-sunken/40"
            >
              <span className="flex items-center gap-2 text-[15px] font-medium">
                <GraduationCap className="h-4 w-4" /> Replay tutorial
              </span>
            </Button>
          </div>
        </section>

        {/* Account */}
        <section>
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
            Account
          </p>
          <div className="overflow-hidden rounded-xl border border-line bg-bg-elevated">
            <Button
              variant="ghost"
              block
              onClick={handleSignOut}
              className="h-14 justify-between rounded-none px-4 text-danger hover:bg-danger-soft/40"
            >
              <span className="flex items-center gap-2 text-[15px] font-medium">
                <LogOut className="h-4 w-4" /> Sign out
              </span>
            </Button>
            <Button
              variant="ghost"
              block
              onClick={() => setDeleteOpen(true)}
              className="h-14 justify-between rounded-none border-t border-line px-4 text-danger hover:bg-danger-soft/40"
            >
              <span className="flex items-center gap-2 text-[15px] font-medium">
                <Trash2 className="h-4 w-4" /> Delete account
              </span>
            </Button>
          </div>
        </section>

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete your account?"
          description="This can't be undone. Your profile and any trip only you are in are deleted. In trips shared with friends you're removed, trips you own pass to another member, and what you added stays under “Former member” so the group's balances still add up."
          confirmLabel="Delete forever"
          destructive
          typeToConfirm="DELETE"
          onConfirm={handleDeleteAccount}
        />

        <p className="text-center text-[11px] text-ink-subtle">
          Travelix v2.0 · {import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "local"}
        </p>
      </div>
    </main>
  );
}
